// vendors
import React, { useState } from 'react';
import { useMutation } from 'react-query';
import { useRouter } from 'routes';
import GroupService from 'services/GroupService';

// lego
import { Icon, Modal } from 'lego-dist';
import { APP_SECTIONS_PATHS } from 'constants/routes';

// views
import DeleteClassroomView from 'views/DeleteClassroomView';
import DeleteClassroomSuccess from 'views/DeleteClassroomView/SuccessView';

// hooks
import { useTranslation } from 'react-i18next';

// react query config
import { classroomQuery } from '../queryConfig';

// styles
import { Header, Title, Dropdown, DropdownItem } from './styles';
import RendererIf from 'components/RendererIf';

const { deleteClassroomQuery } = classroomQuery;

const groupService = GroupService.getInstance();

interface ClassroomDetailsHeaderProps {
  centerId: number;
  externalId?: number;
  groupId: number,
  classroomName: string;
  studentsLength: number;
  dropdownItemKeyMessage?: string;
  canDeleteClassroom: boolean;
}

const ClassroomDetailsHeader: React.FC<ClassroomDetailsHeaderProps> = ({
  centerId,
  groupId,
  classroomName = '',
  studentsLength = 0,
  dropdownItemKeyMessage = 'common:DELETE',
  canDeleteClassroom = false,
}) => {
  const { t: translate } = useTranslation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { navigateTo, currentParamsRef } = useRouter();

  const {
    current: { centerName },
  } = currentParamsRef;

  const [, { isSuccess: isClassroomRemoved }] = useMutation(
    deleteClassroomQuery.resource,
  );

  const deleteClassroom = () => {
    if (!canDeleteClassroom) {
      return;
    }

    groupService.deleteGroupFromCenter(centerId, groupId).then(() => {
      setShowDeleteConfirm(false);
      navigateTo(APP_SECTIONS_PATHS.CENTER_SETTINGS, {
        centerId,
        centerName,
      });
    });
  };

  return (
    <Header>
      <Title>
        {translate('settings:CENTER_SECTION.CLASSROOM_INFORMATION')}
      </Title>
      <RendererIf condition={canDeleteClassroom}>
        <Dropdown
          noPaddingContent
          contentWidth={100}
          showIndicator={false}
          content={({ close }: any) => (
            <DropdownItem
              onClick={() => {
                setShowDeleteConfirm(true);
                close();
              }}
            >
              {translate(dropdownItemKeyMessage)}
            </DropdownItem>
          )}
          title={() => <Icon name="ThreeDots" size="20" />}
        />
      </RendererIf>
      <Modal
        showCloseButton
        headerColor='#0086D8'
        closeButtonColor="#ffffff"
        openModal={showDeleteConfirm}
        title={translate('settings:DELETE_CLASSROOM')}
        onClosedModal={() => setShowDeleteConfirm(false)}
      >
        {() => (
          <DeleteClassroomView
            onDelete={deleteClassroom}
            classroomName={classroomName}
            studentsLength={studentsLength}
            onCancel={() => setShowDeleteConfirm(false)}
          />
        )}
      </Modal>
      <Modal
        showCloseButton
        closeButtonColor="#ACC2D3"
        openModal={isClassroomRemoved}
      >
        {() => <DeleteClassroomSuccess />}
      </Modal>
    </Header>
  );
};

export default ClassroomDetailsHeader;
